import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserRound, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Account() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("remembered_email");
    if (saved) { setEmail(saved); setRemember(true); }
  }, []);

  useEffect(() => {
    if (user) navigate(user.role === "admin" || user.role === "superadmin" ? "/admin" : "/my-map");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = tab === "login" ? await login(email, password) : await register(email, password, name);
    setBusy(false);
    if (res.ok) {
      if (remember) localStorage.setItem("remembered_email", email);
      else localStorage.removeItem("remembered_email");
      const role = res.user?.role;
      navigate(role === "admin" || role === "superadmin" ? "/admin" : "/my-map");
    } else setError(res.error);
  };

  return (
    <div className="mx-auto max-w-md px-4 py-20">
      <div className="bg-card border border-border p-8">
        <div className="flex items-center gap-2 text-accent">
          <Star className="h-6 w-6" />
          <UserRound className="h-6 w-6" />
        </div>
        <h1 className="mt-4 font-serif text-3xl font-bold text-foreground">
          {tab === "login" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Save favorites, keep private notes, and plot your own map of places to explore.
        </p>

        {/* Tabs */}
        <div className="mt-5 flex border border-border" data-testid="account-tabs">
          {["login", "register"].map((t) => (
            <button
              key={t}
              data-testid={`account-tab-${t}`}
              onClick={() => { setTab(t); setError(""); }}
              className="flex-1 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors"
              style={{
                background: tab === t ? "hsl(var(--foreground))" : "transparent",
                color: tab === t ? "hsl(var(--background))" : "hsl(var(--muted-foreground))",
              }}
            >
              {t === "login" ? "Sign In" : "Sign Up"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {tab === "register" && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Name</label>
              <input
                data-testid="account-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                className="mt-1 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent"
              />
            </div>
          )}
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</label>
            <input
              data-testid="account-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Password</label>
            <input
              data-testid="account-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder={tab === "register" ? "At least 6 characters" : ""}
              className="mt-1 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent"
            />
          </div>
          {error && <p data-testid="account-error" className="font-mono text-xs text-destructive">{error}</p>}
          {tab === "login" && (
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="account-remember-label">
              <input
                data-testid="account-remember"
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 accent-[var(--accent)]"
              />
              <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Remember my email</span>
            </label>
          )}
          <button
            data-testid="account-submit"
            type="submit"
            disabled={busy}
            className="w-full bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest py-3.5 hover:bg-accent/90 transition-colors disabled:opacity-60"
          >
            {busy ? "Please wait…" : tab === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
}
